import React from "react";
import { FieldMetadata, getFormProps, useForm } from "@conform-to/react";
import { parseWithZod } from "@conform-to/zod";
import { Box, Button, Typography } from "@mui/material";
import { getSocieteFrequences, updateSocieteFrequences } from "api/societe";
import { useSnackbar } from "pages/Layout/useSnackbar";
import { useEffect, useMemo } from "react";
import {
  ActionFunctionArgs,
  Form,
  LoaderFunctionArgs,
  useActionData,
  useLoaderData,
  useNavigation,
} from "react-router-dom";
import { z } from "zod";
import { useParametrageSociete } from "./Societe";
import { FromError } from "components/FromError";
import { useTranslation } from "react-i18next";
import { SelectConform, SelectPeriodicite } from "components/input/Selects";

const schema = z.object({
  data: z.array(
    z.object({
      typeFrequence: z.string(),
      periodicite: z.string(),
      jour: z.number().optional(),
    })
  ),
});

export type FrequenceForm = z.infer<typeof schema>["data"][0];

export async function loader({ params }: LoaderFunctionArgs) {
  const frequences = await getSocieteFrequences(Number(params.id));
  frequences?.sort((a, b) => a.typeFrequence.localeCompare(b.typeFrequence));
  return { frequences };
}

export async function action({ request, params }: ActionFunctionArgs) {
  const formData = await request.formData();
  const submission = parseWithZod(formData, { schema });
  if (submission.status !== "success") return submission.reply();

  try {
    const PermEnum = {
        AUCUNE: "AUCUNE",
        JOUR: "JOUR",
        SEMAINE: "SEMAINE",
        MOIS: "MOIS",
        TRIMESTRE: "TRIMESTRE",
        ANNEE: "ANNEE",
    } as any;

    const p = submission.value.data?.map((i) => ({
      ...i,
      periodicite: PermEnum[i.periodicite],
    }));
    const data = await updateSocieteFrequences(Number(params.id), p);
    return data;
  } catch (err: any) {
    console.error(err.message);
    return submission.reply({
      formErrors: [err.message],
    });
  }
}

export function Component() {
  const { parametres } = useParametrageSociete();
  const { frequences } = useLoaderData() as Awaited<ReturnType<typeof loader>>;
  const lastResult = useActionData() as any;

  const isIntermediaire = useMemo(() => {
    return parametres?.informations?.type !== undefined && "I" === parametres.informations.type;
  }, [parametres]);

  const [form, fields] = useForm({
    id: JSON.stringify(frequences),
    defaultValue: { data: frequences },
    lastResult,
    shouldValidate: "onBlur",
    shouldRevalidate: "onBlur",
    onValidate({ formData }) {
      return parseWithZod(formData, { schema });
    },
  });

  const frequencesFields = fields.data.getFieldList();
  const navigation = useNavigation();

  const { t } = useTranslation("common");

  const { openSnackbar } = useSnackbar();
  useEffect(() => {
    if (lastResult?.body?.succes) openSnackbar(t("parametrageSociete.modificationReussie"));
  }, [lastResult?.body, openSnackbar]);

  useEffect(() => {
    form.reset();
  }, [frequences]);

  return (
    <Form method="POST" {...getFormProps(form)}>
      <Typography variant="h3" color="primary" mb={2}>
        {t("frequence.titre")}
      </Typography>
      <Box mb={1}>
        {frequencesFields?.map((frequence) => {
          const field = frequence.getFieldset();
          const jour = field.jour;
          const periodicite = field.periodicite;
          const typeFrequence = field.typeFrequence;
          const jourEnum = getJourEnum(periodicite.value as Periodicite);
          return (
            <FrequenceFields
              key={frequence.key}
              jour={jour}
              jourEnum={jourEnum}
              periodicite={periodicite}
              typeFrequence={typeFrequence}
              isIntermediaire={isIntermediaire}
            />
          );
        })}
      </Box>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        <Button type="submit" variant="contained" disabled={navigation.state !== "idle"}>
          {frequencesFields.length > 0 ? "Modifier" : "Initialiser"}
        </Button>
        <FromError errors={form.errors} />
      </Box>
    </Form>
  );
}

export const PeriodiciteEnum = [
  "AUCUNE",
  "JOUR",
  "SEMAINE",
  "MOIS",
  "TRIMESTRE",
  "ANNEE",
] as const;

type Periodicite = (typeof PeriodiciteEnum)[number];

function FrequenceFields({
  jour,
  periodicite,
  typeFrequence,
  isIntermediaire,
  jourEnum,
}: {
  jourEnum: { [key: string]: string };
  jour: FieldMetadata<number>;
  periodicite: FieldMetadata<string>;
  typeFrequence: FieldMetadata<string>;
  isIntermediaire: boolean;
}) {
  const { t } = useTranslation("common");

  return (
    <Box
      mb={2}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 3,
        pb: 1,
        borderBottom: "1px solid #d8d8d8",
      }}
    >
      <input type="hidden" name={typeFrequence.name} value={typeFrequence.initialValue} />
      <Typography width="35%">{t("frequence.code." + typeFrequence.initialValue)}</Typography>

      <SelectPeriodicite label={t("frequence.periodicite.title")} field={periodicite} />
      <SelectConform
        label={t("frequence.jour")}
        field={jour}
        disabled={Object.entries(jourEnum).length <= 1}
        optionsMap={jourEnum}
        clearSelect={false}
      />
    </Box>
  );
}

function getJourEnum(periodicite: Periodicite): { [key: string]: string } {
  switch (periodicite) {
    case "JOUR":
      return { "0": "Tous les jours" };

    case "SEMAINE":
      return Object.fromEntries(
        ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"].map((v, i) => [
          String(i),
          v,
        ])
      );

    case "MOIS":
      return Object.fromEntries([1, 5, 10, 15, 20, 25, 30].map((v, i) => [String(i), String(v)]));

    case "TRIMESTRE":
      return Object.fromEntries(Array.from({ length: 31 }, (_, i) => [String(i), String(i + 1)]));

    case "ANNEE":
      return Object.fromEntries(
        Array.from({ length: 31 }, (_, i) => [String(i), `${i + 1} Janvier`])
      );

    case "AUCUNE":
      return { "0": "0" };

    default:
      throw new Error(`Valeur inattendue pour periodicite : ${periodicite}`);
  }
}
